import {
  useEffect,
  useRef,
  useState,
  type KeyboardEventHandler,
  type MouseEventHandler,
  type ReactNode,
} from "react";
import { Link2, X, ExternalLink, Check, GripVertical } from "lucide-react";
import { useDraggable, useDroppable } from "@dnd-kit/react";
import { createPortal } from "react-dom";
import {
  PointerSensor,
  KeyboardSensor,
  PointerActivationConstraints,
} from "@dnd-kit/dom";
import { useSelection } from "./interaction";
import type { Entity, Kind, Link } from "@kkindex/shared";
import s from "./App.module.css";
const selectionSensors = [
  PointerSensor.configure({
    activationConstraints: () => [
      new PointerActivationConstraints.Distance({ value: 8 }),
    ],
    preventActivation(event, source) {
      if (!source.data.canDrag) return true;
      const target = event.target as Element;
      if (target.closest("[data-selection-key]") !== source.element)
        return true;
      if (event.pointerType !== "mouse") return true;
      return !!target.closest(
        "button,a,textarea,select,input:not([data-inline-title][readonly]),[contenteditable=true],[role=separator],[data-no-drag]",
      );
    },
  }),
  KeyboardSensor.configure({
    preventActivation: (event, source) =>
      !source.data.canDrag || event.target !== source.element,
  }),
];
const navigationSensors = [
  PointerSensor.configure({
    activationConstraints: () => [
      new PointerActivationConstraints.Distance({ value: 7 }),
    ],
    preventActivation(event) {
      return event.pointerType !== "mouse" || event.button !== 0;
    },
  }),
];
export function Modal({
  title,
  children,
  onClose,
  wide = false,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const el = ref.current!;
    el.querySelector<HTMLElement>("input,button,select,textarea,a")?.focus();
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Tab") {
        const controls = [
          ...el.querySelectorAll<HTMLElement>(
            "button,input,select,textarea,a[href]",
          ),
        ].filter(
          (x) => !x.hasAttribute("disabled") && x.getClientRects().length,
        );
        const first = controls[0],
          last = controls.at(-1);
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };
    el.addEventListener("keydown", key);
    return () => {
      el.removeEventListener("keydown", key);
      previous?.focus();
    };
  }, []);
  return createPortal(
    <div
      className={s.overlay}
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`${s.modal} ${wide ? s.wide : ""}`}
      >
        <header>
          <h2>{title}</h2>
          <button aria-label="关闭" onClick={onClose}>
            <X size={19} />
          </button>
        </header>
        {children}
      </div>
    </div>,
    document.body,
  );
}
export function Links({ links, title }: { links: Link[]; title: string }) {
  const [open, setOpen] = useState(false);
  if (!links.length) return null;
  return (
    <>
      <span
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {links.length === 1 ? (
          <a
            className={s.link}
            href={links[0].url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`打开 ${links[0].name || title}`}
          >
            <Link2 size={14} />
          </a>
        ) : (
          <button
            className={s.link}
            aria-label={`${title}的链接`}
            onClick={() => setOpen(true)}
          >
            <Link2 size={14} />
          </button>
        )}
      </span>
      {open && (
        <Modal title="相关链接" onClose={() => setOpen(false)}>
          {links.map((l, i) => (
            <a
              className={s.linkRow}
              key={i}
              href={l.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Link2 size={16} />
              {l.name || new URL(l.url).hostname}
              <ExternalLink size={14} />
            </a>
          ))}
        </Modal>
      )}
    </>
  );
}
export function Checkbox({
  done,
  onClick,
  label,
}: {
  done: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      className={`${s.check} ${done ? s.checked : ""}`}
      aria-label={`${done ? "标记未完成" : "标记完成"}：${label}`}
      aria-pressed={done}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      {done && <Check size={12} />}
    </button>
  );
}
export function Drop({
  id,
  data,
  children,
  className,
  style,
  accept,
  label,
}: {
  id: string;
  data: Record<string, unknown>;
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  accept?: Kind[];
  label?: string;
}) {
  const { ref, isDropTarget } = useDroppable({ id, data, accept });
  return (
    <div
      ref={ref}
      aria-label={label}
      data-date={typeof data.date === "string" ? data.date : undefined}
      className={`${className || ""} ${isDropTarget ? s.dropActive : ""}`}
      style={style}
    >
      {children}
    </div>
  );
}
export function Draggable({
  item,
  id,
  children,
  className = "",
  handle = false,
  disabled = false,
  data = {},
  style,
  selectionKey,
  onOpen,
  onClick,
  onDoubleClick,
  onKeyDown,
  title,
}: {
  item: Entity;
  id: string;
  children: ReactNode;
  className?: string;
  handle?: boolean;
  disabled?: boolean;
  data?: Record<string, unknown>;
  style?: React.CSSProperties;
  selectionKey?: string;
  onOpen?: () => void;
  onClick?: MouseEventHandler<HTMLDivElement>;
  onDoubleClick?: MouseEventHandler<HTMLDivElement>;
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
  title?: string;
}) {
  const { selected, select } = useSelection();
  const active = !!selectionKey && selected === selectionKey;
  const unavailable = disabled || !!item.deletedAt || item.archived;
  const { ref, handleRef, isDragging, isDropping } = useDraggable({
    id,
    type: item.kind,
    data: {
      itemId: item.id,
      kind: item.kind,
      ...data,
      canDrag: active && !unavailable,
    },
    // Gate task activation in the sensors: disabling the container would also
    // mark its checkbox, links and title aria-disabled through dnd-kit's plugin.
    disabled: selectionKey ? false : unavailable,
    ...(selectionKey
      ? { sensors: selectionSensors }
      : item.kind === "site" || item.kind === "group"
        ? { sensors: navigationSensors }
        : {}),
  });
  if (selectionKey)
    return (
      <div
        ref={ref}
        style={style}
        tabIndex={0}
        role="group"
        aria-label={item.title}
        aria-roledescription={active ? "已选中的任务" : "任务"}
        data-item-id={item.id}
        data-selection-key={selectionKey}
        data-selected={active}
        data-drag-state={
          isDragging ? "dragging" : isDropping ? "dropping" : "idle"
        }
        className={`${className} ${s.selectable} ${active ? s.selectedItem : ""} ${isDragging ? s.dragging : ""}`}
        onClick={(event) => {
          if (
            (event.target as Element).closest("[data-selection-key]") !==
            event.currentTarget
          )
            return;
          event.stopPropagation();
          if (
            !(event.target as Element).closest(
              "button,a,input,textarea,select,[data-no-drag]",
            )
          )
            select(selectionKey);
        }}
        onDoubleClick={(event) => {
          if (
            (event.target as Element).closest("[data-selection-key]") !==
              event.currentTarget ||
            (event.target as Element).closest(
              "button,a,input,textarea,select,[role=separator]",
            )
          )
            return;
          event.stopPropagation();
          if (!item.archived) onOpen?.();
        }}
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget) return;
          if (event.key === "Enter") {
            event.preventDefault();
            if (!item.archived) onOpen?.();
          }
          if (event.key === " " && !active) {
            event.preventDefault();
            select(selectionKey);
          }
        }}
      >
        {children}
      </div>
    );
  if (unavailable)
    return (
      <div style={style} className={className} data-item-id={item.id}>
        {children}
      </div>
    );
  return (
    <div
      ref={ref}
      style={style}
      className={`${className} ${isDragging ? s.dragging : ""}`}
      data-item-id={item.id}
      data-drag-state={
        isDragging ? "dragging" : isDropping ? "dropping" : "idle"
      }
    >
      {handle && (
        <button
          ref={handleRef}
          className={s.grip}
          disabled={isDropping}
          aria-label={`拖动 ${item.title}`}
        >
          <GripVertical size={13} />
        </button>
      )}
      {!handle ? (
        <div
          ref={handleRef}
          className={s.dragBody}
          title={title}
          onClick={onClick}
          onDoubleClick={onDoubleClick}
          onKeyDown={onKeyDown}
        >
          {children}
        </div>
      ) : (
        children
      )}
    </div>
  );
}
